/**
 * Compact tab switcher — ONE active-tab pill + a "show all tabs" button, and
 * the vertical tab list it opens. Born in MobileWorkspaceScreen's control row;
 * shared so the desktop TabBar can fall back to the exact same UI when the
 * editor column is too narrow for the strip to show more than one tab per row.
 *
 *   const { pillWrapRef, anchor } = useTabPillAnchor(listOpen)
 *   <CompactTabSwitcher pillWrapRef={pillWrapRef} … />
 *   {listOpen && <TabListSheet anchor={anchor} … />}   // inside a positioned box
 *
 * The sheet fills its nearest positioned ancestor (`absolute inset-0`) and
 * aligns its rows to the pill's measured box, converting the pill's viewport x
 * into its own coordinate space — on a phone the sheet starts at x=0, on
 * desktop it starts at the editor card's left edge.
 */
import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react'
import { GripVertical, MoreVertical, Search, X } from 'lucide-react'
import { FileIcon } from '@papan/core-ui/ui'
import type { Tab } from './useWorkspace'

export interface TabPillAnchor {
  /** Viewport x of the pill's left edge */
  left: number
  width: number
}

/**
 * The list rows follow the opened-tab pill's width + x-position (not a fixed
 * max-width — the pill is narrower than 340px on smaller phones, where a static
 * cap wrongly let rows fill the panel). Measures the pill wrapper and
 * re-measures whenever `open` changes, on resize, and when the wrapper resizes.
 */
export function useTabPillAnchor(open: boolean) {
  const pillWrapRef = useRef<HTMLDivElement>(null)
  const [anchor, setAnchor] = useState<TabPillAnchor | null>(null)
  useLayoutEffect(() => {
    const measure = () => {
      const el = pillWrapRef.current
      if (!el) return
      const r = el.getBoundingClientRect()
      setAnchor({ left: Math.round(r.left), width: Math.round(r.width) })
    }
    measure()
    const ro = new ResizeObserver(measure)
    if (pillWrapRef.current) ro.observe(pillWrapRef.current)
    window.addEventListener('resize', measure)
    return () => { ro.disconnect(); window.removeEventListener('resize', measure) }
  }, [open])
  return { pillWrapRef, anchor }
}

/**
 * Whether the desktop strip should give way to the compact switcher: true when
 * it cannot fit two tabs on one row — the point where it already collapses to
 * a single visible pill. With 2+ tabs that is "every wrapped row holds exactly
 * one pill"; with 1 tab, "a second pill as wide as this one would not fit".
 * The strip must stay mounted (it can be hidden) — it is the measuring source,
 * which is also what keeps the switch from oscillating.
 */
export function useCompactTabStrip({ stripRef, measureKey, onCompactChange }: {
  stripRef: RefObject<HTMLDivElement | null>
  /** Change to force a re-measure (tab order, titles, active tab) */
  measureKey: unknown
  /** Fired with the new value whenever compact flips (and once on mount) */
  onCompactChange?: (compact: boolean) => void
}): boolean {
  const [compact, setCompact] = useState(false)
  useLayoutEffect(() => {
    const strip = stripRef.current
    if (!strip) return
    const measure = () => {
      const pills = Array.from(strip.querySelectorAll<HTMLElement>(':scope > [data-tab-id]'))
      let next = false
      if (pills.length >= 2) {
        // offsetTop ignores transforms, so a reorder drag can't flip this.
        next = new Set(pills.map(p => p.offsetTop)).size === pills.length
      } else if (pills.length === 1) {
        const cs = getComputedStyle(strip)
        const inner = strip.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight)
        next = inner < pills[0].offsetWidth * 2 + (parseFloat(cs.columnGap) || 0)
      }
      setCompact(next)
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(strip)
    return () => ro.disconnect()
  }, [stripRef, measureKey])
  const onChangeRef = useRef(onCompactChange)
  onChangeRef.current = onCompactChange
  useEffect(() => { onChangeRef.current?.(compact) }, [compact])
  return compact
}

interface CompactTabSwitcherProps {
  activeTab: Tab | null
  tabCount: number
  listOpen: boolean
  /** Active look on the pill (the phone dims it while the explorer is open) */
  highlighted: boolean
  pillWrapRef: RefObject<HTMLDivElement | null>
  onPillTap: () => void
  onShowList: () => void
  onOpenMenu: (e: React.MouseEvent, tabId: string) => void
  /** Tapping the placeholder pill when no file is open */
  onEmptyTap: () => void
}

/** [active-tab pill, centered, max 340px] · [boxed tab count] */
export function CompactTabSwitcher({ activeTab, tabCount, listOpen, highlighted, pillWrapRef, onPillTap, onShowList, onOpenMenu, onEmptyTap }: CompactTabSwitcherProps) {
  return (
    <div className="flex min-w-0 flex-1 items-center justify-center gap-1">
      <div ref={pillWrapRef} className="relative min-w-0 flex-1 max-w-[340px]">
        {activeTab ? (
          // Tapping the pill toggles the tab list (the caller decides — the
          // phone only returns to the editor while the explorer is open). The
          // ⋮ still acts on this tab.
          <div
            role="button"
            tabIndex={0}
            onClick={onPillTap}
            // Same guard as the list rows: the pill wraps a real ⋮ <button>
            // that stops propagation on CLICK only, so a keyboard activation
            // of the ⋮ bubbled here and toggled the tab list as well as
            // opening the menu.
            onKeyDown={(e) => {
              if (e.target !== e.currentTarget) return
              if (e.key === 'Enter' || e.key === ' ') onPillTap()
            }}
            // NOT `aria-haspopup="menu"`: this opens the tab LIST, and
            // announcing a menu promises menu keyboard semantics (arrow-key
            // navigation) that this control does not implement. The ⋮ inside
            // is the thing that opens a menu. `aria-expanded` already carries
            // the open/closed state.
            aria-expanded={listOpen}
            className={`ws-pill mobile-tab-pill flex w-full min-w-0 items-center gap-2 px-3 ${
              highlighted ? 'ws-pill-active themed-text' : 'themed-text-secondary'
            }`}
          >
            <FileIcon name={activeTab.title} type={activeTab.type} />
            {/* No text-sm — inherits the pill's bigger font (16px). */}
            <span className="min-w-0 flex-1 truncate">{activeTab.title}</span>
            {activeTab.isDirty && (
              <span className={`h-2 w-2 shrink-0 rounded-full ${activeTab.isConflicted ? 'bg-orange-400' : 'bg-blue-400'}`} />
            )}
            <span className="-mr-1 ml-1 flex items-center">
              <button
                data-tabmenu-trigger
                onClick={(e) => onOpenMenu(e, activeTab.id)}
                aria-label="Tab menu"
                className="rounded p-0.5 opacity-60 active:themed-bg-tertiary"
              >
                <MoreVertical size={12} />
              </button>
            </span>
          </div>
        ) : (
          <button
            onClick={onEmptyTap}
            className="ws-pill mobile-tab-pill flex w-full items-center justify-center gap-2 px-3 themed-text-muted"
          >
            No file open
          </button>
        )}
      </div>
      {tabCount > 1 && (
        <button
          onClick={onShowList}
          // With the count in it, not just "Show all tabs": `aria-label` OVERRIDES
          // the content, so the number inside this button is never announced on
          // its own. The wide strip had the mirror image of this problem — the
          // digits took its name away — and between them a screen reader got the
          // verb or the number but never both.
          //
          // And the verb follows the state, as the wide strip's does: it said
          // "Show all 15 tabs" with the list already open, so the two controls
          // described the same state differently.
          aria-label={listOpen ? 'Collapse tabs' : `Show all ${tabCount} tabs`}
          aria-expanded={listOpen}
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg active:themed-bg-tertiary ${
            listOpen ? 'themed-text' : 'themed-text-muted'
          }`}
        >
          {/* The same boxed count the wide strip uses (TabBar). It was an icon
              with the number tucked against its corner, which read as a badge
              ON something rather than as the control itself. */}
          {/* `min-w` rather than a fixed box: one digit should not sit in a wide
              frame, and the cap keeps it to two digits plus a `+` so nothing
              pokes out of the 32px button. */}
          <span className="text-xs tabular-nums leading-none px-1 py-1 rounded border border-current min-w-[1.25rem] text-center">{tabCount > 99 ? '99+' : tabCount}</span>
        </button>
      )}
    </div>
  )
}

interface TabListSheetProps {
  tabs: Tab[]
  activeTabId: string | null
  anchor: TabPillAnchor | null
  /** Switch to a tab. The caller closes the sheet. */
  onSelect: (tabId: string) => void
  onClose: (tabId: string) => void
  /** Indices are a fallback; pass the drag-start IDs whenever they are known —
   *  see the call site and `useWorkspace.reorderTabs` for why. */
  onReorder: (fromIndex: number, toIndex: number, ids?: { movedId: string; targetId: string }) => void
  onOpenMenu: (e: React.MouseEvent, tabId: string) => void
  onDismiss: () => void
}

/** A row keeps at least this much width so the title survives its fixed
 *  controls (grip, icon, ⋮, ×) even when the pill it follows is narrower —
 *  a narrow desktop editor, or a small phone. */
const MIN_ROW_W = 240
/** Rows never come closer than this to the sheet's edges. */
const ROW_EDGE = 8

/**
 * Tab overview — a frosted overlay with a FLOATING column of tab rows, so the
 * frosted content shows around it. Fills its nearest positioned ancestor.
 * Tapping empty space (or Esc) closes the search first, then the sheet.
 */
export function TabListSheet({ tabs, activeTabId, anchor, onSelect, onClose, onReorder, onOpenMenu, onDismiss }: TabListSheetProps) {
  const [tabQuery, setTabQuery] = useState('')
  // Search: collapsed to JUST the magnifier; tapping it reveals the full
  // field from the right (right edge fixed at the cards' right, width grows
  // leftward) — the icon then sits inside the field. One element throughout.
  const [searchOpen, setSearchOpen] = useState(false)
  // The search UI only EXISTS past a threshold, so "open" has to be derived
  // against that. Otherwise closing tabs down to the threshold while search was
  // open unmounted the field AND its close button while `searchOpen` stayed
  // true — leaving the main list hidden behind a search that was no longer on
  // screen.
  const searchAvailable = tabs.length > 8
  const searchActive = searchOpen && searchAvailable
  const searchInputRef = useRef<HTMLInputElement>(null)
  // Side effects live in the HANDLER, not inside the state updater. Updaters
  // must be pure: <StrictMode> (on in main.tsx) double-invokes them in dev, so
  // a setTimeout/setState in there fires twice. `searchOpen` is read directly
  // here (a plain click handler, no batched-update race to lose to).
  const toggleSearch = () => {
    const next = !searchOpen
    setSearchOpen(next)
    if (next) setTimeout(() => searchInputRef.current?.focus(), 60)
    else setTabQuery('')
  }
  const dismiss = () => {
    if (searchActive) { setSearchOpen(false); setTabQuery('') }
    else onDismiss()
  }
  // Deriving alone heals only DOWNWARD. Crossing back above the threshold
  // would otherwise revive a search the user never reopened — `searchOpen` and
  // the old query survived, so closing a tab to 8 (list returns) and opening
  // one back to 9 made the field and its stale results reappear over the list.
  // Cleared here rather than in `dismiss`, which only runs while the search is
  // actually on screen.
  useEffect(() => {
    if (!searchAvailable && searchOpen) {
      setSearchOpen(false)
      setTabQuery('')
    }
  }, [searchAvailable, searchOpen])

  const dismissRef = useRef(dismiss)
  dismissRef.current = dismiss
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') dismissRef.current() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // The anchor is in viewport x; the rows are laid out inside this sheet. The
  // sheet's own box is observed (not just window resize): on desktop the
  // editor card changes width when a side panel is dragged.
  const rootRef = useRef<HTMLDivElement>(null)
  const [rootBox, setRootBox] = useState<{ left: number; width: number } | null>(null)
  useLayoutEffect(() => {
    const el = rootRef.current
    if (!el) return
    const measure = () => {
      const r = el.getBoundingClientRect()
      setRootBox({ left: Math.round(r.left), width: Math.round(r.width) })
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    window.addEventListener('resize', measure)
    return () => { ro.disconnect(); window.removeEventListener('resize', measure) }
  }, [])
  let rowsStyle: React.CSSProperties = { marginInline: 'auto', maxWidth: 340 }
  if (anchor && rootBox) {
    const width = Math.min(Math.max(anchor.width, MIN_ROW_W), rootBox.width - ROW_EDGE * 2)
    const marginLeft = Math.min(Math.max(anchor.left - rootBox.left, ROW_EDGE), rootBox.width - width - ROW_EDGE)
    rowsStyle = { width, marginLeft }
  }

  // Drag-to-reorder. A dedicated grip handle owns the gesture so it never
  // fights the list scroll or the row's tap-to-switch. Pointer-based (works on
  // touch), driven entirely by DIRECT DOM writes through refs — NO React state
  // on the hot path — so a drag doesn't re-render the workspace (which would
  // re-render every mounted editor: the lag). The array is reordered EXACTLY
  // ONCE, on drop; mid-drag the rows are shifted with transforms (reordering
  // tabs live would move the editor panes' iframes in the DOM → reload).
  //
  // `ids` is the tab order AS OF GRAB TIME, snapshotted here — the same thing
  // `useTabReorder.measure()` does on desktop. Reading `tabs[startIndex]` at
  // DROP instead looks right and is not: the handler is a JSX prop, so React
  // installs a fresh closure every render, and a stale index into a newly
  // shortened array resolves to a DIFFERENT tab. That is worse than passing no
  // ids at all, because `reorderTabs` drops a reorder whose ids no longer
  // resolve — and two confidently wrong ids resolve fine.
  const dragRef = useRef<{ startY: number; startIndex: number; curTarget: number; rowH: number; ids: string[] } | null>(null)
  const rowRefs = useRef<(HTMLElement | null)[]>([])
  const onGripDown = (e: React.PointerEvent, index: number) => {
    e.stopPropagation()
    const rowEl = rowRefs.current[index]
    const rowH = (rowEl?.offsetHeight ?? 38) + 6 // + gap-1.5 (6px) between rows
    dragRef.current = { startY: e.clientY, startIndex: index, curTarget: index, rowH, ids: tabs.map(t => t.id) }
    if (rowEl) {
      rowEl.style.position = 'relative'
      rowEl.style.zIndex = '30'
      rowEl.style.opacity = '0.97'
      rowEl.style.boxShadow = '0 8px 20px rgba(0,0,0,0.35)'
    }
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId) } catch { /* synthetic */ }
  }
  const onGripMove = (e: React.PointerEvent) => {
    const d = dragRef.current
    if (!d) return
    const dy = e.clientY - d.startY
    const dragged = rowRefs.current[d.startIndex]
    if (dragged) dragged.style.transform = `translateY(${dy}px)` // follows the finger 1:1
    const target = Math.max(0, Math.min(tabs.length - 1, d.startIndex + Math.round(dy / d.rowH)))
    if (target === d.curTarget) return
    d.curTarget = target
    // Shift the rows between the grab slot and the target to open a gap where the
    // dragged row will land. Pure transforms — tabs are untouched until drop.
    for (let i = 0; i < rowRefs.current.length; i++) {
      if (i === d.startIndex) continue
      const el = rowRefs.current[i]
      if (!el) continue
      let shift = 0
      if (target > d.startIndex && i > d.startIndex && i <= target) shift = -d.rowH
      else if (target < d.startIndex && i >= target && i < d.startIndex) shift = d.rowH
      el.style.transition = 'transform 0.14s ease'
      el.style.transform = shift ? `translateY(${shift}px)` : ''
    }
  }
  const onGripUp = (e: React.PointerEvent) => {
    const d = dragRef.current
    if (!d) return
    dragRef.current = null
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId) } catch { /* already released */ }
    // Clear every inline drag style BEFORE the reorder so the re-render lands in
    // the right place with no leftover transform (transition off → no drop flash).
    for (const el of rowRefs.current) {
      if (!el) continue
      el.style.transition = 'none'
      el.style.transform = ''
      el.style.position = ''
      el.style.zIndex = ''
      el.style.opacity = ''
      el.style.boxShadow = ''
    }
    if (d.curTarget !== d.startIndex) {
      // IDs from the GRAB-TIME snapshot, never from the live `tabs` prop. A tab
      // closed mid-drag (a realtime `file:deleted`, another pane) shifts every
      // index after it, so both a raw index and a fresh-array lookup name the
      // wrong file — silently, because every position involved is still valid.
      // `reorderTabs` resolves by id and drops the reorder when one has gone,
      // which only works if the ids describe the order the user actually saw.
      const movedId = d.ids[d.startIndex]
      const targetId = d.ids[d.curTarget]
      onReorder(d.startIndex, d.curTarget, movedId && targetId ? { movedId, targetId } : undefined)
    }
    // Re-enable transitions on the next frame (after the reorder commit).
    requestAnimationFrame(() => { for (const el of rowRefs.current) if (el) el.style.transition = '' })
  }

  // Row renderer for the main list AND the search-results panel. The main list
  // is bordered cards + ⋮ menu + a drag grip. The search RESULTS (asResult) are
  // plain rows — border + background on the SELECTED one only — no grip, no ⋮
  // menu, just the × close. w-full — the parent sets width. In a sheet under
  // 300px (`@container/tablist`) the touch-sized 32px buttons shrink to 24px
  // and the gaps tighten, leaving the title room to be read and clicked.
  const btnSize = 'h-8 w-8 @max-[300px]/tablist:h-6 @max-[300px]/tablist:w-6'
  const renderTabRow = (tab: Tab, opts?: { asResult?: boolean; index?: number }) => (
    <div
      key={tab.id}
      data-tabrow
      ref={opts?.index != null ? (el) => { rowRefs.current[opts.index!] = el } : undefined}
      role="button"
      tabIndex={0}
      onClick={() => onSelect(tab.id)}
      // `e.target !== e.currentTarget` — the row wraps real <button>s (grip, ⋮,
      // close) whose handlers stop propagation on CLICK only. A keyboard
      // activation is a different event and bubbled here first, so pressing
      // Enter on a background tab's × selected that tab AND closed it, landing
      // the user on a third tab they never chose.
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return
        if (e.key === 'Enter' || e.key === ' ') onSelect(tab.id)
      }}
      className={`flex w-full items-center gap-2.5 rounded-lg border px-2.5 py-1.5 @max-[300px]/tablist:gap-1.5 @max-[300px]/tablist:px-1.5 ${
        tab.id === activeTabId
          ? 'themed-border themed-bg-tertiary themed-text'
          : opts?.asResult
            ? 'border-transparent themed-text-secondary active:themed-bg-tertiary'
            : 'themed-border-secondary themed-bg-secondary themed-text-secondary active:themed-bg-tertiary'
      }`}
    >
      {!opts?.asResult && opts?.index != null && (
        <button
          onPointerDown={(e) => onGripDown(e, opts.index!)}
          onPointerMove={onGripMove}
          onPointerUp={onGripUp}
          onPointerCancel={onGripUp}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Reorder ${tab.title}`}
          // touch-none so dragging the handle reorders instead of scrolling the list.
          className="-ml-1 flex h-8 w-5 shrink-0 touch-none cursor-grab items-center justify-center rounded-md themed-text-muted active:cursor-grabbing"
        >
          <GripVertical size={16} />
        </button>
      )}
      <FileIcon name={tab.title} type={tab.type} size={16} />
      <span className={`min-w-0 flex-1 truncate text-sm${tab.deleted ? ' line-through opacity-60' : ''}`} data-tab-deleted={tab.deleted ? 'true' : undefined}>{tab.title}</span>
      {tab.isDirty && (
        <span className={`h-2 w-2 shrink-0 rounded-full ${tab.isConflicted ? 'bg-orange-400' : 'bg-blue-400'}`} />
      )}
      {!opts?.asResult && (
        <button
          data-tabmenu-trigger
          onClick={(e) => onOpenMenu(e, tab.id)}
          aria-label={`Menu for ${tab.title}`}
          className={`flex ${btnSize} shrink-0 items-center justify-center rounded-md themed-text-muted active:themed-bg-tertiary`}
        >
          <MoreVertical size={17} />
        </button>
      )}
      <button
        onClick={(e) => { e.stopPropagation(); onClose(tab.id) }}
        aria-label={`Close ${tab.title}`}
        className={`flex ${btnSize} shrink-0 items-center justify-center rounded-md themed-text-muted active:themed-bg-tertiary`}
      >
        <X size={17} />
      </button>
    </div>
  )

  return (
    <div ref={rootRef} className="@container/tablist absolute inset-0 z-20">
      <div className="mobile-fog mobile-fog-deep mobile-fog-in absolute inset-0" onClick={dismiss} aria-hidden />
      {/* No panel background — the rows float straight over the fog. The panel
          spans the full width so children can be positioned by x: the search
          stays wide, the rows align to the measured pill box. Tapping empty
          space inside closes the search first, otherwise the whole list. */}
      <div className="mobile-sheet-in absolute inset-x-0 top-0 bottom-0 flex flex-col" onClick={dismiss}>
        {/* The list fills the panel and scrolls; the search floats ABSOLUTELY
            over it (below) so the rows pass BEHIND it. Hidden the moment the
            search field opens — the search (and, once typed, the results panel)
            takes over. */}
        <div
          className="min-h-0 flex-1 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          style={{ display: searchActive ? 'none' : undefined }}
        >
          <div className="flex flex-col gap-1.5 pt-3 pb-4" style={rowsStyle}>
            {tabs.map((t, i) => renderTabRow(t, { index: i }))}
          </div>
        </div>
        {/* Search — ABSOLUTE, floats over the top-right. Collapsed = a 36px icon
            at right:40; open = full width with 12px margins, the icon riding to
            the right edge. */}
        {searchAvailable && (
          <div
            className="absolute top-2 z-10 h-9 transition-[left,right] duration-200 ease-out"
            // Don't let taps on the field / search toggle bubble to the sheet's
            // background-close handler.
            onClick={(e) => e.stopPropagation()}
            style={searchOpen ? { left: 12, right: 12 } : { left: 'calc(100% - 76px)', right: 40 }}
          >
            <input
              ref={searchInputRef}
              value={tabQuery}
              onChange={(e) => setTabQuery(e.target.value)}
              placeholder="Search tabs…"
              aria-label="Search tabs"
              aria-hidden={!searchOpen}
              tabIndex={searchOpen ? 0 : -1}
              // text-base (16px): iOS/Chrome auto-zoom on focus only fires for
              // inputs under 16px, so this keeps the keyboard from zooming in.
              className={`themed-input h-9 w-full rounded-lg border pl-3 pr-9 text-base transition-opacity duration-150 ${
                searchOpen ? 'themed-border opacity-100' : 'pointer-events-none border-transparent opacity-0'
              }`}
              // Frosted field: translucent fill + backdrop-blur so the rows
              // behind the search read as blurred.
              style={searchOpen ? {
                background: 'color-mix(in srgb, var(--bg-secondary) 60%, transparent)',
                backdropFilter: 'blur(10px) saturate(1.1)',
                WebkitBackdropFilter: 'blur(10px) saturate(1.1)',
              } : undefined}
            />
            <button
              onClick={toggleSearch}
              aria-label={searchOpen ? 'Close search' : 'Search tabs'}
              aria-expanded={searchOpen}
              className="absolute inset-y-0 right-0 flex w-9 items-center justify-center rounded-lg themed-text-muted active:themed-bg-tertiary"
            >
              <Search size={18} />
            </button>
          </div>
        )}
        {/* Search RESULTS — a SEPARATE panel that drops BELOW the search field
            (not an in-place filter, which stranded the top match behind the
            floating search). Its own solid card, above the unfiltered list. */}
        {searchActive && tabQuery.trim() && (() => {
          const q = tabQuery.trim().toLowerCase()
          const matches = tabs.filter((t) => t.title.toLowerCase().includes(q))
          return (
            <div className="absolute inset-x-3 top-11 z-[5] flex max-h-[calc(100%-3rem)] flex-col gap-1.5 overflow-y-auto px-4 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {matches.length > 0
                ? matches.map((t) => renderTabRow(t, { asResult: true }))
                : <p className="px-2 py-4 text-center text-sm themed-text-muted">No matching tabs</p>}
            </div>
          )
        })()}
      </div>
    </div>
  )
}
